import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AwsCtx = createContext(null);

export function awsErr(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.code) return `${d.code}: ${d.message}`;
  if (typeof d === "string") return d;
  return e?.message || "Something went wrong.";
}

export function AwsProvider({ children }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/aws/state");
    setState(data);
    return data;
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const run = useCallback(
    async (fn, { success } = {}) => {
      try {
        const res = await fn();
        await refresh();
        if (success) toast.success(success);
        return { ok: true, res };
      } catch (e) {
        toast.error(awsErr(e));
        return { ok: false, error: awsErr(e) };
      }
    },
    [refresh]
  );

  const action = useCallback((name, body) => api.post(`/aws/action/${name}`, body), []);
  const setRegion = useCallback(
    (region) => run(() => api.post("/aws/region", { region }), {}),
    [run]
  );
  const switchIdentity = useCallback(
    (identity_id) => run(() => api.post("/aws/identity", { identity_id }), {}),
    [run]
  );

  const identity = state?.iam?.identities?.find((i) => i.is_current);
  const region = state?.current_region;
  const regions = state?.catalog?.regions || [];

  // convenience resource selectors scoped to current region
  const byType = (t) =>
    (state?.resources || []).filter(
      (r) => r.type === t && r.state !== "terminated" && (r.region === region || t === "bucket")
    );

  return (
    <AwsCtx.Provider
      value={{ state, loading, refresh, run, action, setRegion, switchIdentity, identity, region, regions, byType }}
    >
      {children}
    </AwsCtx.Provider>
  );
}

export const useAws = () => useContext(AwsCtx);
